import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import classNames from "classnames";
import "./index.scss";

import { getHours, getMinutes } from "../../../utils/convertTime";

import IconButton from "../IconButton";
import Dropdown from "../Dropdown";

const LapseControl = ({ t, column, value, onPropChange }) => {
    const [currentValue, setCurrentValue] = useState([]);
    const [isOpen, setIsOpen] = useState([]);
    const [editItems, setEditItems] = useState([]);
    const hours = getHours();
    const minutes = getMinutes();

    const getFormattedTime = ({ open, close }) => {
        return `${open.hour < 10 ? `0` + open.hour : open.hour}:` + 
            `${open.minute < 10 ? `0` + open.minute : open.minute}-` +
            `${close.hour < 10 ? `0` + close.hour : close.hour}:` + 
            `${close.minute < 10 ? `0` + close.minute : close.minute}`
    }

    const getEditingValue = (index, isMinute, fromClose) => {
        const time = editItems[index] && editItems[index][fromClose ? "close" : "open"][isMinute ? "minute" : "hour"];
        const formattedValue = (isMinute ? minutes : hours).find(f=> f.key === time) || {};
        return formattedValue.value;
    }

    // the close can't come before the opening hour; the opening hour itself is offered
    // only while a minute is left after the opening minute
    const getCloseHours = index => {
        const open = editItems[index] && editItems[index].open;
        return open
            ? hours.filter(hour=> hour.key > open.hour || (hour.key === open.hour && open.minute < 59))
            : hours;
    }

    // within the opening hour the close can only fall after the opening minute
    const getCloseMinutes = index => {
        const lapse = editItems[index];
        return lapse && lapse.close.hour === lapse.open.hour
            ? minutes.filter(minute=> minute.key > lapse.open.minute)
            : minutes;
    }

    const handleOnAdd = () => {
        return new Promise(resolve=> {
            const data = { open: { hour: 0, minute: 0 }, close: { hour: 23, minute: 59 }};

            resolve();
            setCurrentValue(value.concat(data))
            setIsOpen(isOpen.concat(true));
            setEditItems(editItems.concat(data));
        });
    }

    const handleOnEdit = index => {
        return new Promise(resolve=> {
            resolve();
            if(!column.isReadonly){
                setIsOpen(isOpen.map((m, i)=> i === index ? true : m));
                setEditItems(editItems.map((m, i)=> i === index ? value[index] : m));
            }
        });
    }

    const handleOnDelete = index => {
        return new Promise(resolve=> {
            const data = value.filter((f, i)=> i !== index);

            resolve();
            onPropChange({ [column.accessor]: data });
            setCurrentValue(data);
            setIsOpen(isOpen.filter((f, i)=> i !== index));
            setEditItems(editItems.filter((f, i)=> i !== index));
        });
    }

    const handleOnChangeLapse = (index, hour, minute, isClose) => {
        const propName = !isClose ? "open" : "close";
        const result =  editItems.map((editItem, i)=> i === index 
            ? ({ ...editItem, [propName]: { 
                hour: hour === null ? editItem[propName].hour : hour,
                minute: minute === null ? editItem[propName].minute : minute,
            }})
            : editItem
        );

        setEditItems(result);
    }

    const handleOnApply = index => {
        return new Promise(resolve=> {
            resolve();

            const data = currentValue.map((val, i)=> i === index ? editItems[index] : val);
            onPropChange({ [column.accessor]: data });
            setCurrentValue(data);
            setEditItems(editItems.map((m, i)=> i === index ? null : m));
            setIsOpen(isOpen.map((m, i)=> i === index ? false : m));           
        });
    }

    const handleOnCancel = index => {
        return new Promise(resolve=> {
            resolve();

            if(value.length - 1 < index) {
                setCurrentValue(currentValue.filter((f, i)=> i !== index));
                setEditItems(editItems.filter((f, i)=> i !== index));
                setIsOpen(isOpen.filter((f, i)=> i !== index));
            } else {
                setEditItems(editItems.map((m, i)=> i === index ? null : m));
                setIsOpen(isOpen.map((m, i)=> i === index ? false : m));
            }
        });
    }

    useEffect(()=> {
        if(value && value.length && !isOpen.length) {
            setCurrentValue(value);
            setIsOpen(value.map(()=> (false)));
            setEditItems(value.map(()=> (null)));
        }
    }, [value, isOpen]);

    return (
        <div className={classNames("lapseControl")}>
            <table>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>{t("settings.orderingmenu.MENU_BUSINESS_SHEDULE_LAPSE_TIME_LABEL")}</th>
                        <th><IconButton isIcon={true} iconName={"iconAdd"} tooltipText={column.addTooltipText} onClick={handleOnAdd} /></th>
                    </tr>
                </thead>
                <tbody>
                    {(currentValue || []).map((lapse, index)=> (
                        <tr key={`lapse-` + index}>
                            <td>{index + 1}</td>
                            <td className={classNames({ open: isOpen[index] })}>   
                                <div className={classNames("headTitle")}>{getFormattedTime(lapse)}</div>                             
                                <div className={classNames("lapseEditItems")}>
                                    <div className={classNames("lapseEditItem")}>
                                        <div className={classNames("title")}>
                                            {t("settings.orderingmenu.MENU_BUSINESS_SHEDULE_LAPSE_TIME_OPEN_LABEL")}
                                        </div>
                                        <div className={classNames("time")}>
                                            <Dropdown values={hours} value={getEditingValue(index)}
                                                isRequired={column.isRequired}
                                                onSelect={val => handleOnChangeLapse(index, val.key, null)}
                                            />
                                            &nbsp;:&nbsp;
                                            <Dropdown values={minutes} value={getEditingValue(index, true)}
                                                isRequired={column.isRequired}
                                                onSelect={val => handleOnChangeLapse(index, null, val.key)}
                                            />
                                        </div>
                                    </div>
                                    <div className={classNames("lapseEditItem")}>
                                        <div className={classNames("title")}>
                                            {t("settings.orderingmenu.MENU_BUSINESS_SHEDULE_LAPSE_TIME_CLOSE_LABEL")}
                                        </div>
                                        <div className={classNames("time")}>
                                            <Dropdown value={getEditingValue(index, false, true)}
                                                isRequired={column.isRequired}
                                                values={getCloseHours(index)}
                                                onSelect={val => handleOnChangeLapse(index, val.key, null, true)}
                                            />
                                            &nbsp;:&nbsp;
                                            <Dropdown values={getCloseMinutes(index)} value={getEditingValue(index, true, true)}
                                                isRequired={column.isRequired}
                                                onSelect={val => handleOnChangeLapse(index, null, val.key, true)}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className={classNames("footer")}>
                                    {isOpen[index] && (
                                        <IconButton isIcon={true} iconName={"apply"} tooltipText={t("basic.APPLY_BUTTON")} onClick={()=> handleOnApply(index)} />
                                    )}
                                    {isOpen[index] && (
                                        <IconButton isIcon={true} iconName={"cancel"} tooltipText={t("basic.CANCEL_BUTTON")} onClick={()=> handleOnCancel(index)} />
                                    )}
                                </div>
                            </td>
                            <td>
                                {!isOpen[index] && (
                                    <IconButton isIcon={true} iconName={"orderEdit"} tooltipText={column.editTooltipText}
                                        onClick={()=> handleOnEdit(index)} 
                                    />
                                )}
                                {!isOpen[index] && (
                                    <IconButton isIcon={true} iconName={"orderDelete"} tooltipText={column.deleteTooltipText}
                                        onClick={()=> handleOnDelete(index)} 
                                    />
                                )}                                
                            </td>
                        </tr>
                    ))}
                    
                </tbody>
            </table>
        </div>
    );
}

export default withTranslation()(LapseControl);